import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  Image as ImageIcon,
  Plus,
  Trash2,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  MousePointerClick,
  X,
  AlertTriangle,
  Pencil,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import AdminLayout from "../layout/AdminLayout";
import { db, storage } from "../../firebase";

function compressBannerImage(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith("image/")) { reject(new Error("Not an image")); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        const maxWidth = 1400;
        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width));
          width = maxWidth;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(new File([blob], file.name, { type: "image/jpeg" })) : reject(new Error("Compression failed")),
          "image/jpeg",
          0.85
        );
      };
      img.onerror = () => reject(new Error("Could not load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

const AUDIENCE_OPTIONS = [
  { value: "all", label: "All users" },
  { value: "free", label: "Free users only" },
  { value: "premium", label: "Premium users only" },
];

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ title: "", redirectUrl: "", file: null, audience: "all" });
  const [preview, setPreview] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editBanner, setEditBanner] = useState(null);
  const [editData, setEditData] = useState({ title: "", redirectUrl: "", audience: "all" });
  const [saving, setSaving] = useState(false);

  const loadBanners = async () => {
    try {
      const snap = await getDocs(query(collection(db, "banners"), orderBy("order", "asc")));
      setBanners(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch {
      try {
        const snap = await getDocs(collection(db, "banners"));
        setBanners(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0)));
      } catch {
        setBanners([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const queryClient = useQueryClient();
  const bannersQuery = useQuery({
    queryKey: ["adminBanners"],
    queryFn: async () => {
      try {
        const snap = await getDocs(query(collection(db, "banners"), orderBy("order", "asc")));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch {
        const snap = await getDocs(collection(db, "banners"));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => (a.order || 0) - (b.order || 0));
      }
    },
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  useEffect(() => {
    if (bannersQuery.data) setBanners(bannersQuery.data);
    setLoading(bannersQuery.isLoading);
  }, [bannersQuery.data, bannersQuery.isLoading]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormData((f) => ({ ...f, file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.file) return;
    setUploading(true);
    try {
      const compressed = await compressBannerImage(formData.file);
      const fileName = `banners/${Date.now()}_${formData.file.name.replace(/[^a-zA-Z0-9.]/g, "_")}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, compressed);
      const imageUrl = await getDownloadURL(storageRef);

      const newOrder = banners.length > 0 ? Math.max(...banners.map((b) => b.order || 0)) + 1 : 0;

      await addDoc(collection(db, "banners"), {
        title: formData.title || "Banner",
        imageUrl,
        storagePath: fileName,
        redirectUrl: formData.redirectUrl || "",
        audience: formData.audience || "all",
        active: true,
        order: newOrder,
        clicks: 0,
        createdAt: serverTimestamp(),
      });

      setFormData({ title: "", redirectUrl: "", file: null, audience: "all" });
      setPreview("");
      setShowForm(false);
      toast.success("Banner added!");
      queryClient.invalidateQueries(["adminBanners"]);
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleActive = async (banner) => {
    try {
      await updateDoc(doc(db, "banners", banner.id), { active: !banner.active });
      setBanners((prev) => prev.map((b) => b.id === banner.id ? { ...b, active: !b.active } : b));
      toast.success(banner.active ? "Banner hidden" : "Banner visible");
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
      queryClient.invalidateQueries(["adminBanners"]);
  };

  const moveOrder = async (banner, direction) => {
    const idx = banners.findIndex((b) => b.id === banner.id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= banners.length) return;
    const other = banners[swapIdx];
    try {
      // Swap order values using index positions
      await updateDoc(doc(db, "banners", banner.id), { order: swapIdx });
      await updateDoc(doc(db, "banners", other.id), { order: idx });
      // Immediately swap in local state for instant UI feedback
      const updated = [...banners];
      updated[idx] = { ...other, order: idx };
      updated[swapIdx] = { ...banner, order: swapIdx };
      setBanners(updated);
      toast.success("Order updated");
    } catch (err) {
      toast.error(err.message || "Reorder failed");
    }
      queryClient.invalidateQueries(["adminBanners"]);
  };

  const openEdit = (banner) => {
    setEditBanner(banner);
    setEditData({ title: banner.title || "", redirectUrl: banner.redirectUrl || "", audience: banner.audience || "all" });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editBanner) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "banners", editBanner.id), {
        title: editData.title || "Banner",
        redirectUrl: editData.redirectUrl || "",
        audience: editData.audience || "all",
      });
      setBanners((prev) => prev.map((b) => b.id === editBanner.id ? { ...b, ...editData } : b));
      setEditBanner(null);
      toast.success("Banner updated!");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
      queryClient.invalidateQueries(["adminBanners"]);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      if (confirmDelete.storagePath) {
        await deleteObject(ref(storage, confirmDelete.storagePath)).catch(() => {});
      }
      await deleteDoc(doc(db, "banners", confirmDelete.id));
      setBanners((prev) => prev.filter((b) => b.id !== confirmDelete.id));
      toast.success("Banner deleted");
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
      queryClient.invalidateQueries(["adminBanners"]);
  };

  const getAudienceLabel = (val) => AUDIENCE_OPTIONS.find((o) => o.value === val)?.label || "All users";

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-800">Ad Banners</h1>
            <p className="mt-0.5 text-[13px] text-ink-400">{banners.length} banner{banners.length !== 1 ? "s" : ""} · {banners.filter((b) => b.active).length} active</p>
          </div>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary shrink-0">
            <Plus size={15} /> New Banner
          </button>
        </div>

        {/* ===== ADD BANNER MODAL ===== */}
        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 px-4 backdrop-blur-md" onClick={() => !uploading && setShowForm(false)}>
              <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-5 shadow-elevated">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-lg font-bold text-ink-800">New Banner</h2>
                  <button type="button" onClick={() => setShowForm(false)} className="btn-icon h-8 w-8"><X size={15} /></button>
                </div>
                <form onSubmit={handleUpload} className="space-y-3">
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Banner Image *</label>
                    <div className="mt-1">
                      {preview ? (
                        <div className="relative rounded-xl overflow-hidden border border-sand-200">
                          <img src={preview} alt="Preview" className="w-full h-auto" />
                          <button type="button" onClick={() => { setPreview(""); setFormData((f) => ({ ...f, file: null })); }} className="absolute top-1.5 right-1.5 h-6 w-6 flex items-center justify-center rounded-full bg-black/60 text-white"><X size={12} /></button>
                        </div>
                      ) : (
                        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sand-300 bg-sand-50/50 py-6 transition hover:border-brand-600" onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-brand-600"); }} onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-brand-600"); }} onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove("border-brand-600"); const file = e.dataTransfer.files?.[0]; if (file && file.type.startsWith("image/")) { setFormData((f) => ({ ...f, file })); setPreview(URL.createObjectURL(file)); } }}>
                          <ImageIcon size={20} className="text-ink-400" />
                          <p className="text-[12px] font-semibold text-ink-600">Drop or click to upload</p>
                          <p className="text-[10.5px] text-ink-400">1200 × 400px recommended</p>
                          <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                        </label>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Title</label>
                    <input type="text" value={formData.title} onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Monsoon Sale" className="input mt-1 text-[13px]" />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Redirect URL</label>
                    <input type="url" value={formData.redirectUrl} onChange={(e) => setFormData((f) => ({ ...f, redirectUrl: e.target.value }))} placeholder="https://example.com" className="input mt-1 text-[13px]" />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Show to</label>
                    <select value={formData.audience} onChange={(e) => setFormData((f) => ({ ...f, audience: e.target.value }))} className="input mt-1 text-[13px]">
                      {AUDIENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <button type="submit" disabled={!formData.file || uploading} className="btn-primary w-full py-3 text-[13px]">
                    {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading...</> : <><Plus size={14} /> Add Banner</>}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== EDIT BANNER MODAL ===== */}
        <AnimatePresence>
          {editBanner && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 px-4 backdrop-blur-md" onClick={() => !saving && setEditBanner(null)}>
              <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white p-5 shadow-elevated">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display text-lg font-bold text-ink-800">Edit Banner</h2>
                  <button type="button" onClick={() => setEditBanner(null)} className="btn-icon h-8 w-8"><X size={15} /></button>
                </div>
                <div className="mb-4 rounded-xl overflow-hidden border border-sand-200">
                  <img src={editBanner.imageUrl} alt={editBanner.title} className="w-full h-auto" />
                </div>
                <form onSubmit={handleSaveEdit} className="space-y-3">
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Title</label>
                    <input type="text" value={editData.title} onChange={(e) => setEditData((f) => ({ ...f, title: e.target.value }))} className="input mt-1 text-[13px]" />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Redirect URL</label>
                    <input type="url" value={editData.redirectUrl} onChange={(e) => setEditData((f) => ({ ...f, redirectUrl: e.target.value }))} className="input mt-1 text-[13px]" />
                  </div>
                  <div>
                    <label className="text-[12px] font-semibold text-ink-600">Show to</label>
                    <select value={editData.audience} onChange={(e) => setEditData((f) => ({ ...f, audience: e.target.value }))} className="input mt-1 text-[13px]">
                      {AUDIENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <button type="submit" disabled={saving} className="btn-primary w-full py-3 text-[13px]">
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== DELETE CONFIRM MODAL ===== */}
        <AnimatePresence>
          {confirmDelete && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 px-4 backdrop-blur-md" onClick={() => !deleting && setConfirmDelete(null)}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-3xl bg-white p-5 shadow-elevated text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600"><AlertTriangle size={22} /></div>
                <h3 className="mt-3 font-display text-[16px] font-bold text-ink-800">Delete Banner?</h3>
                <p className="mt-1.5 text-[12.5px] text-ink-500">"{confirmDelete.title}" will be permanently deleted.</p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <button type="button" disabled={deleting} onClick={() => setConfirmDelete(null)} className="rounded-2xl border border-sand-200 bg-white py-2.5 text-[13px] font-semibold text-ink-700 transition hover:bg-sand-50">Cancel</button>
                  <button type="button" disabled={deleting} onClick={handleDelete} className="rounded-2xl bg-rose-600 py-2.5 text-[13px] font-semibold text-white transition hover:bg-rose-700">{deleting ? "Deleting..." : "Delete"}</button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== BANNERS LIST ===== */}
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-brand-600" /></div>
        ) : banners.length === 0 ? (
          <div className="card flex flex-col items-center p-10 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-sand-100 text-ink-300"><ImageIcon size={32} /></div>
            <p className="mt-4 font-display text-[16px] font-bold text-ink-700">No banners yet</p>
            <p className="mt-1 text-[13px] text-ink-400">Create your first ad banner</p>
            <button type="button" onClick={() => setShowForm(true)} className="btn-primary mt-5"><Plus size={15} /> Create Banner</button>
          </div>
        ) : (
          <div className="space-y-3">
            {banners.map((banner, idx) => (
              <div key={banner.id} className={`card overflow-hidden ${!banner.active ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-4 p-4">
                  {/* Order number */}
                  <div className="flex flex-col items-center gap-1 shrink-0">
                    <button type="button" onClick={() => moveOrder(banner, -1)} disabled={idx === 0} className="text-ink-400 hover:text-ink-700 disabled:opacity-20 disabled:cursor-not-allowed transition">
                      <ChevronUp size={16} />
                    </button>
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-[13px] font-bold text-brand-700">{idx + 1}</span>
                    <button type="button" onClick={() => moveOrder(banner, 1)} disabled={idx === banners.length - 1} className="text-ink-400 hover:text-ink-700 disabled:opacity-20 disabled:cursor-not-allowed transition">
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  {/* Thumbnail */}
                  <div className="w-32 sm:w-44 shrink-0 overflow-hidden rounded-xl border border-sand-200">
                    <img src={banner.imageUrl} alt={banner.title} className="w-full h-auto" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-[14px] font-bold text-ink-800 truncate">{banner.title || "Untitled"}</h3>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${banner.active ? "bg-emerald-50 text-emerald-700" : "bg-sand-100 text-ink-400"}`}>
                        {banner.active ? "Live" : "Hidden"}
                      </span>
                    </div>
                    {banner.redirectUrl && (
                      <a href={banner.redirectUrl} target="_blank" rel="noopener noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-[11.5px] text-brand-600 hover:underline">
                        <ExternalLink size={10} /> <span className="truncate max-w-[160px] inline-block">{banner.redirectUrl.replace(/^https?:\/\//, "")}</span>
                      </a>
                    )}
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-400">
                      <span className="inline-flex items-center gap-1"><MousePointerClick size={10} /> {banner.clicks || 0}</span>
                      <span className="inline-flex items-center gap-1">{getAudienceLabel(banner.audience)}</span>
                    </div>

                    {/* Actions */}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <button type="button" onClick={() => toggleActive(banner)} className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold transition ${banner.active ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50"}`}>
                        {banner.active ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                      </button>
                      <button type="button" onClick={() => openEdit(banner)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold text-ink-600 hover:bg-sand-100 transition">
                        <Pencil size={12} /> Edit
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(banner)} className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50 transition">
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
